import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { ImportLog } from "../../types/core/ImportLog";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import Area from "../../types/core/Area";

// Define the type for our state store entry
type AreaState = {
    archives: Area[];
    areas: Area[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: AreaState = {
    archives: [],
    areas: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const areaSlice = createSlice({
    name: "area",
    initialState: initialState,
    reducers: {
        resetAreaStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    archives:
                        rawData?.payload?.area?.archives?.map(
                            c => new Area(c),
                        ) || [],
                    areas:
                        rawData?.payload?.area?.areas?.map(c => new Area(c)) ||
                        [],
                    status: !rawData?.payload?.area?.areas
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchAreas.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchAreas.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.areas = action.payload[0].map(c => new Area(c));
                state.archives = action.payload[1].map(c => new Area(c));
            })
            .addCase(fetchAreas.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addArea.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addArea.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.areas = state.areas.concat(
                    action.payload.map(c => new Area(c)),
                );
            })
            .addCase(addArea.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateArea.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateArea.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const areaIds = action.payload.map(c => c.id);
                state.areas = state.areas
                    .filter(c => !areaIds.includes(c.id))
                    .concat(action.payload.map(c => new Area(c)));
            })
            .addCase(updateArea.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(archiveArea.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(archiveArea.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const areaIds = action.payload.map(c => c.id);
                state.areas = state.areas.filter(c => !areaIds.includes(c.id));
                state.archives = state.archives.concat(
                    action.payload.map(c => new Area(c)),
                );
            })
            .addCase(archiveArea.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetAreaStatus } = areaSlice.actions;

export const initialiseAreaDetails = createAsyncThunk(
    "area/initialiseAreaDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchAreas()),
);

// This is a thunk, they are used to load the state
export const fetchAreas = createAsyncThunk(
    "area/fetchAreas",
    () =>
        Promise.all([
            Datastore.getType<Area[]>("api/Areas"),
            Datastore.getType<Area[]>("api/Areas/Archive"),
        ]),
    {
        condition: (_, api) =>
            store.getState().db.area.status === "idle",
    },
);
export const addArea = createAsyncThunk(
    "area/addArea",
    (payload: Area[]) => Datastore.postType<Area[]>("api/Areas", payload),
    {
        condition: (_, api) =>
            store.getState().db.area.status === "succeeded",
    },
);
export const updateArea = createAsyncThunk(
    "area/updateArea",
    (payload: Area[]) => Datastore.putType<Area[]>("api/Areas", payload),
    {
        condition: (_, api) =>
            store.getState().db.area.status === "succeeded",
    },
);
export const archiveArea = createAsyncThunk(
    "area/archiveArea",
    (payload: Area[]) => {
        Datastore.post(
            "api/Areas/Archive",
            payload.map(c => c.id),
        );
        return payload;
    },
    {
        condition: (_, api) =>
            store.getState().db.area.status === "succeeded",
    },
);

/*******     Importing     *******/
export const importAreasFromTD = async (id: number) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Areas/Import/TD/${id}`,
    );

    store.dispatch(resetAreaStatus());
    await unwrappedDispatch(fetchAreas());

    return result;
};

// We have to export the reducer as this is added to the state store
export default areaSlice.reducer;
